/**
 *  @class
 *  @function CollectionTabs
 */

if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();
      this._onResize = null;
      this._onBlockSelect = null;
      this._fetchCache = new Map();
      this._fetchPromises = new Map();
    }

    connectedCallback() {
      requestAnimationFrame(() => {
        this.buttons = Array.from(this.querySelectorAll('button'));
        let sectionHeader = this.closest('.section-header');
        this.links = sectionHeader ? Array.from(sectionHeader.querySelectorAll('.linked-to-tab')) : [];
        this.target = this.dataset.target;
        this.slider = document.getElementById(this.target);

        if (!this.slider) return;

        this.productLimit = parseInt(this.slider.dataset.productLimit, 10) || 6;
        // Cache items grouped by collection handle
        this.collectionMap = new Map();
        this.allItems = this.slider.querySelectorAll('[data-collection]');
        this.allItems.forEach((item) => {
          let handle = item.dataset.collection;
          if (!this.collectionMap.has(handle)) {
            this.collectionMap.set(handle, []);
          }
          this.collectionMap.get(handle).push(item);
        });

        // Event delegation instead of per-button listeners
        this.addEventListener('click', (event) => {
          let button = event.target.closest('button');
          if (!button) return;
          event.preventDefault();
          let index = this.buttons.indexOf(button);
          if (index === -1) return;
          this.toggleButtons(index);
          this.toggleLinks(index);
          if (button.dataset.collection) {
            this.handleTabClick(button);
          }
        });

        // Debounce resize to avoid excessive Flickity recalculations
        this._onResize = debounce(() => {
          let flkty = this.getFlickity();
          if (flkty) {
            flkty.resize();
          }
        }, 150);
        window.addEventListener('resize', this._onResize);

        if (Shopify.designMode) {
          this._onBlockSelect = (event) => {
            const index = this.buttons.indexOf(event.target);
            if (index !== -1) {
              this.buttons[index].dispatchEvent(new Event('click'));
            }
          };
          this.addEventListener('shopify:block:select', this._onBlockSelect);
        }

        // Lazy-load inactive tabs after page load
        if (!Shopify.designMode) {
          this.prefetchInactiveTabs();
        }

        this.bindQuickAdd(this.slider);
        this.bindSwatches(this.slider);

        // Homepage tabs: Flickity viewport can stay ~17px tall on mobile until images/fonts settle.
        this._ksUserMoved = false;
        this.bindCarouselNav();
        this.refreshCarousel();
        window.addEventListener(
          'load',
          () => {
            this.refreshCarousel();
          },
          { once: true }
        );
      });
    }

    disconnectedCallback() {
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
      }
      if (this._onBlockSelect) {
        this.removeEventListener('shopify:block:select', this._onBlockSelect);
      }
    }

    getFlickity() {
      if (!this.slider) return null;
      return Flickity.data(this.slider) || null;
    }

    toggleButtons(index) {
      this.buttons.forEach((button) => {
        button.classList.remove('active');
        button.setAttribute('aria-pressed', 'false');
      });
      this.buttons[index].classList.add('active');
      this.buttons[index].setAttribute('aria-pressed', 'true');
    }

    getCartSectionsToRender() {
      return [
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }

    getSectionInnerHTML(html, selector) {
      if (!html) return '';
      const el = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector);
      return el ? el.innerHTML : '';
    }

    renderCartContents(parsedState) {
      if (!parsedState || !parsedState.sections) return;

      this.getCartSectionsToRender().forEach(({ id, section, selector }) => {
        const el = document.getElementById(id);
        if (!el) return;
        const html = this.getSectionInnerHTML(parsedState.sections[section], selector);
        if (!html) return;
        const target = el.querySelector(selector) || el;
        target.innerHTML = html;
      });

      const cartDrawer = document.getElementById('Cart-Drawer');
      if (!cartDrawer || typeof cartDrawer.open !== 'function') return;

      const productDrawer = document.getElementById('Product-Drawer');
      if (productDrawer && typeof OverlayManager !== 'undefined' && OverlayManager.isOpen(productDrawer)) {
        OverlayManager.close(productDrawer);
      }
      cartDrawer.open();
    }

    bindSwatches(root) {
      if (!root || root.dataset.ksSwatchBound) return;
      root.dataset.ksSwatchBound = '1';

      const stopCarouselDrag = (event) => {
        if (event.target.closest('.ks-ccard .product-card-swatch')) {
          event.stopPropagation();
        }
      };
      root.addEventListener('pointerdown', stopCarouselDrag, true);
      root.addEventListener('mousedown', stopCarouselDrag, true);
      root.addEventListener('touchstart', stopCarouselDrag, { capture: true, passive: true });

      root.addEventListener('click', (event) => {
        const swatch = event.target.closest('.ks-ccard .product-card-swatch');
        if (!swatch) return;
        event.preventDefault();
        event.stopPropagation();
        this.applyCardSwatch(swatch);
      });

      root.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const swatch = event.target.closest('.ks-ccard .product-card-swatch');
        if (!swatch) return;
        event.preventDefault();
        this.applyCardSwatch(swatch);
      });
    }

    applyCardSwatch(swatch) {
      const card = swatch.closest('.ks-ccard');
      if (!card) return;

      const group = swatch.closest('.product-card-swatches');
      if (group) {
        group.querySelectorAll('.product-card-swatch.active').forEach((el) => {
          el.classList.remove('active');
          el.setAttribute('aria-pressed', 'false');
        });
      }
      swatch.classList.add('active');
      swatch.setAttribute('aria-pressed', 'true');

      const image = card.querySelector('.ks-ccard__image');
      const srcset = (swatch.dataset.srcset || '').trim();
      if (image && srcset) {
        const firstSrc = srcset.split(',')[0].trim().split(/\s+/)[0];
        image.setAttribute('srcset', srcset);
        if (firstSrc) {
          image.setAttribute('src', firstSrc);
          if (image.hasAttribute('data-src')) image.setAttribute('data-src', firstSrc);
          if (image.hasAttribute('data-srcset')) image.setAttribute('data-srcset', srcset);
        }
        image.classList.remove('lazyload', 'lazyloading');
        image.classList.add('lazyloaded');
      }

      if (swatch.dataset.href) {
        const media = card.querySelector('.ks-ccard__media');
        const title = card.querySelector('.ks-ccard__name a');
        const buy = card.querySelector('a.ks-ccard__buy');
        if (media) media.setAttribute('href', swatch.dataset.href);
        if (title) title.setAttribute('href', swatch.dataset.href);
        if (buy) buy.setAttribute('href', swatch.dataset.href);
      }

      const addBtn = card.querySelector('[data-ks-add-to-cart]');
      if (addBtn && swatch.dataset.variantId) {
        addBtn.dataset.variantId = swatch.dataset.variantId;
        addBtn.disabled = swatch.dataset.available === 'false';
      }

      if (swatch.dataset.priceFormatted) {
        const priceEl = card.querySelector('.ks-ccard__price');
        if (priceEl) {
          priceEl.replaceChildren();
          priceEl.append(swatch.dataset.priceFormatted);
          if (swatch.dataset.compareFormatted) {
            priceEl.append(' ');
            const was = document.createElement('s');
            was.textContent = swatch.dataset.compareFormatted;
            priceEl.appendChild(was);
          }
        }
      }
    }

    bindQuickAdd(root) {
      if (!root) return;
      root.querySelectorAll('[data-ks-add-to-cart]').forEach((btn) => {
        if (btn.dataset.ksBound) return;
        btn.dataset.ksBound = '1';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (btn.disabled || btn.classList.contains('loading')) return;

          let variantId = btn.dataset.variantId;
          if (!variantId || !window.theme || !theme.routes) return;

          btn.classList.add('loading');

          let formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', 1);
          formData.append('sections', this.getCartSectionsToRender().map((section) => section.section).join(','));
          formData.append('sections_url', window.location.pathname);

          const addPromise = (typeof window.fetchCartAdd === 'function')
            ? window.fetchCartAdd(formData)
            : fetch(`${theme.routes.cart_add_url}`, {
                method: 'POST',
                headers: {
                  'X-Requested-With': 'XMLHttpRequest',
                  'Accept': 'application/javascript'
                },
                body: formData
              }).then((r) => r.text()).then((text) => {
                const parsed = (typeof window.parseShopifyJson === 'function')
                  ? window.parseShopifyJson(text)
                  : JSON.parse(text);
                if (!parsed) throw new Error('cart-json');
                return parsed;
              });

          addPromise
            .then((response) => {
              if (typeof window.applyCartAddResponse === 'function') {
                window.applyCartAddResponse(response);
              } else {
                if (response.status) throw new Error(response.description || response.message || 'cart-add');
                this.renderCartContents(response);
              }
              document.dispatchEvent(new CustomEvent('cart:item-added', {
                detail: { product: response.hasOwnProperty('items') ? response.items[0] : response }
              }));
            })
            .catch((err) => {
              console.error(err);
              if (typeof window.showCartError === 'function') {
                window.showCartError(btn, err && err.message);
              }
            })
            .finally(() => btn.classList.remove('loading'));
        });
      });
    }

    toggleLinks(index) {
      if (this.links.length && this.links[index]) {
        this.links.forEach((link) => link.classList.remove('active'));
        this.links[index].classList.add('active');
      }
    }

    bindCarouselNav() {
      const markMoved = () => {
        this._ksUserMoved = true;
      };
      if (!this._ksArrowBound && this.slider && this.slider.parentNode) {
        const arrows = this.slider.parentNode.querySelectorAll('.flickity-prev, .flickity-next');
        if (arrows.length) {
          arrows.forEach((btn) => {
            btn.addEventListener('pointerdown', markMoved);
            btn.addEventListener('click', markMoved);
          });
          this._ksArrowBound = true;
        }
      }
      const flkty = this.getFlickity();
      if (flkty && !this._ksFlktyBound) {
        this._ksFlktyBound = true;
        flkty.on('dragStart', markMoved);
        flkty.on('change', () => {
          if (!this._ksSyncing) this._ksUserMoved = true;
        });
      }
    }

    refreshCarousel() {
      this.bindCarouselNav();
      let flkty = this.getFlickity();
      if (!flkty || !this.slider) return;

      let syncViewport = () => {
        let cards = this.slider.querySelectorAll('.ks-home-tabs__slide.carousel__slide .ks-ccard');
        cards.forEach((card) => {
          card.style.minHeight = '';
        });

        if (flkty.selectedIndex > 0) this._ksUserMoved = true;
        let index = flkty.selectedIndex;
        this._ksSyncing = true;
        flkty.reloadCells();
        if (!this._ksUserMoved) index = 0;
        let last = Math.max(0, (flkty.slides && flkty.slides.length ? flkty.slides.length : flkty.cells.length) - 1);
        flkty.select(Math.min(index, last), false, true);
        flkty.resize();

        let cardMax = 0;
        cards.forEach((card) => {
          cardMax = Math.max(cardMax, card.offsetHeight);
        });
        if (cardMax >= 240) {
          cards.forEach((card) => {
            card.style.minHeight = cardMax + 'px';
          });
          flkty.resize();
        }

        let vp = flkty.viewport;
        if (vp && flkty.cells.length) {
          let max = 0;
          flkty.cells.forEach((cell) => {
            max = Math.max(max, cell.element.getBoundingClientRect().height);
          });
          if (max > 0 && vp.getBoundingClientRect().height < max * 0.75) {
            vp.style.height = max + 'px';
            flkty.resize();
          }
        }
        this._ksSyncing = false;
      };

      requestAnimationFrame(() => {
        syncViewport();
        requestAnimationFrame(syncViewport);
      });

      let schedule = () => {
        if (this._ksRefreshRAF) return;
        this._ksRefreshRAF = requestAnimationFrame(() => {
          this._ksRefreshRAF = 0;
          syncViewport();
        });
      };

      this.slider.querySelectorAll('img').forEach((img) => {
        if (img.complete || img.dataset.ksBestRefresh === '1') return;
        img.dataset.ksBestRefresh = '1';
        img.addEventListener('load', schedule, { once: true });
      });
      if (typeof lazySizes !== 'undefined' && lazySizes.loader) {
        this.slider.querySelectorAll('.lazyload').forEach((img) => {
          if (img.dataset.ksBestLazyRefresh === '1') return;
          img.dataset.ksBestLazyRefresh = '1';
          img.addEventListener('lazyloaded', schedule, { once: true });
        });
      }
      if (document.fonts && document.fonts.ready && !this._ksFontsRefreshBound) {
        this._ksFontsRefreshBound = true;
        document.fonts.ready.then(schedule);
      }
    }

    toggleCollection(handle) {
      if (!this.slider) return;
      let flkty = this.getFlickity();
      let activeItems = this.collectionMap.get(handle) || [];
      if (!activeItems.length) return;

      let flickitySlider = flkty ? this.slider.querySelector('.flickity-slider') : null;
      activeItems.forEach((item) => {
        item.classList.add('carousel__slide');
        if (flickitySlider && item.parentNode !== flickitySlider) {
          flickitySlider.appendChild(item);
        }
      });

      this.allItems.forEach((item) => {
        if (activeItems.indexOf(item) === -1) {
          item.classList.remove('carousel__slide');
        }
      });

      this._ksUserMoved = false;
      this.refreshCarousel();
    }

    updateViewAll(button) {
      const viewAll = this.closest('.ks-best') && this.closest('.ks-best').querySelector('[data-ks-best-view-all]');
      if (viewAll && button && button.dataset.collectionUrl) {
        viewAll.setAttribute('href', button.dataset.collectionUrl);
      }
    }

    handleTabClick(button) {
      this.updateViewAll(button);
      let handle = button.dataset.collection;
      let cachedItems = this.collectionMap.get(handle);
      if (cachedItems && cachedItems.length) {
        this.toggleCollection(handle);
        return;
      }
      // Products not loaded yet — fetch on demand
      let url = button.dataset.collectionUrl;
      if (!url) return;
      this.fetchCollection(handle, url).then(() => {
        if ((this.collectionMap.get(handle) || []).length) {
          this.toggleCollection(handle);
        }
      });
    }

    prefetchInactiveTabs() {
      let loadTabs = () => {
        this.buttons.forEach((button, index) => {
          if (index === 0) return;
          let handle = button.dataset.collection;
          let url = button.dataset.collectionUrl;
          if (!handle || !url || this.collectionMap.has(handle)) return;
          this.fetchCollection(handle, url);
        });
      };

      if (document.readyState === 'complete') {
        loadTabs();
      } else {
        window.addEventListener('load', loadTabs, { once: true });
      }
    }

    fetchCollection(handle, collectionUrl) {
      if (this._fetchCache.has(handle)) {
        return Promise.resolve();
      }
      if (this._fetchPromises.has(handle)) {
        return this._fetchPromises.get(handle);
      }

      let fetchUrl = collectionUrl + '?section_id=collection-tabs-ajax';
      let promise = fetch(fetchUrl)
        .then((response) => response.text())
        .then((html) => {
          let parser = new DOMParser();
          let doc = parser.parseFromString(html, 'text/html');
          let items = Array.from(doc.querySelectorAll('[data-collection]')).slice(0, this.productLimit);

          // Check for a pre-rendered promotion template for this collection
          let sectionId = this.slider.dataset.id;
          let promotionTemplate = document.querySelector(
            `template[data-promotion-section="${sectionId}"][data-promotion-collection="${handle}"]`
          );
          let promotionItems = [];
          if (promotionTemplate) {
            let promoClone = promotionTemplate.content.cloneNode(true);
            promotionItems = Array.from(promoClone.querySelectorAll('[data-collection]'));
            promotionTemplate.remove();
          }

          let allNewItems = [...promotionItems, ...items];
          let activeButton = this.querySelector('button.active');
          let isActiveCollection = activeButton && activeButton.dataset.collection === handle;

          // Insert into the Flickity slider container (or slider root if Flickity not initialized)
          let flickitySlider = this.slider.querySelector('.flickity-slider');
          let container = flickitySlider || this.slider;
          allNewItems.forEach((item) => {
            if (!isActiveCollection) {
              item.classList.remove('carousel__slide');
            }
            container.appendChild(item);
          });

          // Initialize lazy-loaded images
          container.querySelectorAll('.lazyload').forEach((img) => {
            lazySizes.loader.unveil(img);
          });

          // Update collectionMap and allItems
          if (!this.collectionMap.has(handle)) {
            this.collectionMap.set(handle, []);
          }
          allNewItems.forEach((item) => {
            this.collectionMap.get(handle).push(item);
          });
          this.allItems = this.slider.querySelectorAll('[data-collection]');
          this.bindQuickAdd(container);
          this._fetchCache.set(handle, true);
          this._fetchPromises.delete(handle);
          if (isActiveCollection) {
            this.refreshCarousel();
          }
        })
        .catch(() => {
          this._fetchPromises.delete(handle);
        });

      this._fetchPromises.set(handle, promise);
      return promise;
    }
  }
  customElements.define('collection-tabs', CollectionTabs);
}
